import React, { useState, useEffect, useCallback } from 'react';
import { useSearchParams, useNavigate } from 'react-router-dom';
import { api } from '../../shared/utils/api';
import ROUTES from '../../shared/constants/routes';
import QuestionStats from './QuestionStats';
import QuestionSession from './QuestionSession';
import QuestionReviewModal from './QuestionReviewModal';
import TopicPicker from './TopicPicker';

const MODULES = [
  { value: 'both', label: 'Both' },
  { value: 'FLK1', label: 'FLK1' },
  { value: 'FLK2', label: 'FLK2' },
];

const MODES = [
  { value: 'weakest', label: 'Weakest topics' },
  { value: 'topic', label: 'Specific topic' },
  { value: 'random', label: 'Random' },
  { value: 'incorrect', label: 'Review incorrect' },
];

const MODE_DESCS = {
  weakest: 'Focus on your lowest-scoring areas',
  topic: 'Choose an L2 area or L3 subtopic',
  random: 'Questions from across all topics',
  incorrect: 'Retry questions you got wrong',
};

function fmtDate(isoStr) {
  if (!isoStr) return '';
  const d = new Date(isoStr);
  return d.toLocaleDateString('en-GB', { day: 'numeric', month: 'short' });
}

export default function QuestionsPage() {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const [stats, setStats] = useState(null);
  const [statsLoading, setStatsLoading] = useState(true);
  const [history, setHistory] = useState([]);
  const [historyLoading, setHistoryLoading] = useState(true);
  const [topicCounts, setTopicCounts] = useState([]);
  const [topicCountsLoading, setTopicCountsLoading] = useState(false);
  const [selectedTopicIds, setSelectedTopicIds] = useState(new Set());

  const initMode = searchParams.get('mode') || 'weakest';
  const initTopicId = searchParams.get('topic_id');
  const [module, setModule] = useState('both');
  const [mode, setMode] = useState(initMode);
  const [sessionType, setSessionType] = useState('practice');
  const [sessionConfig, setSessionConfig] = useState(null);
  const [reviewQuestionId, setReviewQuestionId] = useState(null);
  const [showAllHistory, setShowAllHistory] = useState(false);

  useEffect(() => {
    if (initTopicId) setSelectedTopicIds(new Set([parseInt(initTopicId, 10)]));
  }, [initTopicId]);

  // Auto-start if URL has mode=topic and topic_id
  useEffect(() => {
    if (initMode === 'topic' && initTopicId) {
      setSessionConfig({ module: 'both', mode: 'topic', topicIds: [parseInt(initTopicId, 10)], count: 10, sessionType: 'practice' });
    }
  }, []); // eslint-disable-line

  const loadStats = useCallback(() => {
    setStatsLoading(true);
    api('/api/questions/stats')
      .then(data => { setStats(data); setStatsLoading(false); })
      .catch(() => setStatsLoading(false));
  }, []);

  useEffect(() => {
    loadStats();
    api('/api/questions/history?per_page=10')
      .then(data => { setHistory(data.items || []); setHistoryLoading(false); })
      .catch(() => setHistoryLoading(false));
  }, [loadStats]);

  // Load topic counts for hierarchical topic picker
  useEffect(() => {
    if (mode !== 'topic') return;
    setTopicCountsLoading(true);
    api('/api/questions/topic-counts')
      .then(d => { setTopicCounts(d.topics || []); setTopicCountsLoading(false); })
      .catch(() => setTopicCountsLoading(false));
  }, [mode]);

  const canStart = mode !== 'topic' || selectedTopicIds.size > 0;

  const handleStart = () => {
    setSessionConfig({ module, mode, topicIds: [...selectedTopicIds], count: 10, sessionType });
  };

  const handleSessionExit = (opts = {}) => {
    setSessionConfig(null);
    loadStats();
    if (opts.reviewIncorrect) {
      setTimeout(() => setSessionConfig({ module, mode: 'incorrect', topicId: null, count: 10, sessionType }), 50);
    }
  };

  if (sessionConfig) {
    return (
      <div className="page-container page-container-wide">
        <QuestionSession config={sessionConfig} onExit={handleSessionExit} />
      </div>
    );
  }

  return (
    <div className="page-container">
      <h1 className="page-title">MCQ practice</h1>

      <QuestionStats stats={stats} loading={statsLoading} />

      {/* Controls */}
      <div className="qp-controls">
        <div className="qp-controls-title">Start a session</div>

        <div className="qp-row-label">Module</div>
        <div className="qp-selector-row">
          {MODULES.map(m => (
            <button
              key={m.value}
              className={`qp-selector-btn ${module === m.value ? 'active' : ''}`}
              onClick={() => setModule(m.value)}
            >
              {m.label}
            </button>
          ))}
        </div>

        <div className="qp-row-label">What to practise</div>
        <div className="qp-mode-grid">
          {MODES.map(m => (
            <div
              key={m.value}
              className={`qp-mode-card ${mode === m.value ? 'qp-mode-card-selected' : ''}`}
              onClick={() => { setMode(m.value); if (m.value !== 'topic') { setSelectedTopicIds(new Set()); } }}
            >
              <div className="qp-mode-card-name">{m.label}</div>
              <div className="qp-mode-card-desc">{MODE_DESCS[m.value]}</div>
            </div>
          ))}
        </div>

        {mode === 'topic' && (
          <div className="qp-picker-wrapper">
            {topicCountsLoading ? (
              <div className="qp-empty">Loading topics</div>
            ) : (
              <TopicPicker
                topics={topicCounts}
                selectedIds={selectedTopicIds}
                onToggle={(topicId) => setSelectedTopicIds(prev => {
                  const next = new Set(prev);
                  if (next.has(topicId)) next.delete(topicId);
                  else next.add(topicId);
                  return next;
                })}
              />
            )}
            {selectedTopicIds.size > 0 && (
              <div className="qp-picker-selected">
                {selectedTopicIds.size} topic{selectedTopicIds.size !== 1 ? 's' : ''} selected
              </div>
            )}
          </div>
        )}

        <div className="qp-row-label">Session type</div>
        <div>
          <div className="qp-seg-control">
            <button className={`qp-seg-opt ${sessionType === 'practice' ? 'qp-seg-opt-on' : ''}`} onClick={() => setSessionType('practice')}>
              Practice
            </button>
            <button className={`qp-seg-opt ${sessionType === 'exam' ? 'qp-seg-opt-on' : ''}`} onClick={() => setSessionType('exam')}>
              Exam conditions
            </button>
          </div>
          <div className="qp-seg-hint">
            {sessionType === 'practice' ? 'Immediate feedback after each question' : 'No feedback until the end. Timed.'}
          </div>
        </div>

        <button
          className="btn-primary"
          onClick={handleStart}
          disabled={!canStart}
        >
          Start session
        </button>
      </div>

      {/* Weakest topics */}
      {stats && stats.weakest_topics && stats.weakest_topics.length > 0 && (
        <div className="qp-weakest-section">
          <div className="qp-section-label">Topics to work on</div>
          <div className="qp-weakest-list">
            {stats.weakest_topics.map(t => {
              const scoreClass = t.accuracy < 50 ? 'qp-weak-score-red' : t.accuracy < 75 ? 'qp-weak-score-amber' : 'qp-weak-score-green';
              return (
                <div key={t.general_topic} className="qp-weak-card" onClick={() => {
                  if (t.module && t.module.startsWith('FLK2')) navigate(ROUTES.FLK2 + (t.topic_id ? `?focus=${t.topic_id}` : ''));
                  else navigate(ROUTES.FLK1 + (t.topic_id ? `?focus=${t.topic_id}` : ''));
                }}>
                  <span className="qp-weak-name">{t.general_topic}</span>
                  <span className="qp-weak-count">{t.attempted}q</span>
                  <span className={`qp-weak-score ${scoreClass}`}>
                    {t.accuracy}%
                  </span>
                </div>
              );
            })}
          </div>
        </div>
      )}

      {/* Recent history */}
      <div className="qp-history-section">
        <div className="qp-history-title">Recent attempts</div>
        <div className="qp-history-card">
          {historyLoading ? (
            <div className="qp-empty">Loading</div>
          ) : history.length === 0 ? (
            <div className="qp-empty">No attempts yet. Start a session above.</div>
          ) : (
            <div className="qp-history-list">
              {(showAllHistory ? history : history.slice(0, 5)).map(item => (
                <div
                  key={item.id}
                  className="qp-history-item qp-history-item-clickable"
                  onClick={() => setReviewQuestionId(item.question_id)}
                >
                  <div className={`qp-history-dot ${item.correct ? 'qp-history-dot-correct' : 'qp-history-dot-incorrect'}`} />
                  <span className="qp-history-stem">{item.stem_preview}</span>
                  <span className="qp-history-topic">{item.subtopic}</span>
                  <span className="qp-history-topic">{fmtDate(item.answered_at)}</span>
                </div>
              ))}
              {history.length > 5 && (
                <button className="qp-history-toggle" onClick={() => setShowAllHistory(v => !v)}>
                  {showAllHistory ? 'Show less \u2191' : `View all attempts \u2192`}
                </button>
              )}
            </div>
          )}
        </div>
      </div>

      {reviewQuestionId && (
        <QuestionReviewModal
          questionId={reviewQuestionId}
          onClose={() => setReviewQuestionId(null)}
        />
      )}
    </div>
  );
}
